import React, { createRef, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { PortalContextProvider } from '@amzn/storm-ui';
import {
  DEVICE_TYPE,
  selectDeviceType,
  selectIsMobileWebView,
  selectViewType,
  VIEW_TYPE,
} from 'src/app/slice';
import { selectIsSpoofing } from './store/slice';
import {
  useChromeConfigurationListener,
  useDomRefById,
} from 'src/commons/hooks';
import { RootState } from 'src/app/store';
import {
  selectInternalLogoImage,
  selectLogoImage,
} from 'src/features/nav/store/slice';
import { ImageProps } from 'src/commons/utils/types';
import { MobileHeader } from './Header.mobile';
import { DesktopHeader } from './Header.desktop';
import { WebviewHeader } from './Header.webview';
import { useMobileViewPortHook } from './UtilityComponents';
import { markComponentReady } from 'src/commons/utils/logWithTakt';
import { Components } from 'src/commons/constants';
import { shouldRenderMenu } from 'src/commons/utils/helpers';
import { fetchAdvertisingApiCsrfToken } from 'src/app/slice.actions';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';

export const Header: React.VFC<{
  viewType: VIEW_TYPE;
  isSpoofing: boolean;
  logo: ImageProps;
  internalLogo?: ImageProps;
  deviceType: DEVICE_TYPE;
  isMobileWebView: boolean;
  isWebComponentPath?: boolean;
}> = ({
  viewType,
  isSpoofing,
  logo,
  internalLogo,
  deviceType,
  isMobileWebView,
  isWebComponentPath = false,
}) => {
  const chromeConfig = useChromeConfigContext();
  const pageLayoutType = chromeConfig.pageLayoutType;
  const portalRef = useDomRefById('AACChromePortal');
  const headerPortalRef = isWebComponentPath
    ? createRef<HTMLDivElement>()
    : undefined;
  const isMobile = viewType === VIEW_TYPE.MOBILE;
  const dispatch = useDispatch();
  useChromeConfigurationListener();

  useMobileViewPortHook({
    deviceType,
    mobileWebSupport: chromeConfig.mobileWebSupport,
  });

  useEffect(function () {
    markComponentReady(Components.HEADER);
    dispatch(fetchAdvertisingApiCsrfToken());
  }, []);

  // Header to be used in Seller App
  if (isMobileWebView) {
    return (
      <WebviewHeader pageLayoutType={pageLayoutType} isSpoofing={isSpoofing} />
    );
  }

  if (isMobile) {
    return (
      <MobileHeader pageLayoutType={pageLayoutType} isSpoofing={isSpoofing} />
    );
  }

  return (
    <>
      {isWebComponentPath && (
        <div id="AACChromeHeaderPortal" ref={headerPortalRef}></div>
      )}
      <PortalContextProvider
        portalElementRef={isWebComponentPath ? headerPortalRef : portalRef}
      >
        <DesktopHeader
          logo={logo}
          internalLogo={internalLogo}
          shouldRenderMenu={shouldRenderMenu(viewType, pageLayoutType)}
          pageLayoutType={pageLayoutType}
        />
      </PortalContextProvider>
    </>
  );
};
const headerConnector = connect((state: RootState) => {
  return {
    deviceType: selectDeviceType(state),
    viewType: selectViewType(state),
    isSpoofing: selectIsSpoofing(state),
    logo: selectLogoImage(state),
    internalLogo: selectInternalLogoImage(state),
    isMobileWebView: selectIsMobileWebView(state),
  };
});

export const ConnectedHeader = headerConnector(Header);
